import HttpRequest from '@/config/axios.config'
import { getUrlParamsFromObject } from '../TaskTable/TaskTable.helper'
import { isEmptyValue, transformFieldEmptyToNull } from '@/utils/common'
import { isEmpty, map, omitBy, join, includes, replace } from 'lodash'
import moment from 'moment'
import { DATE_FORMAT } from '@/constant/date'
import { ApiPaginatedResponse } from '@/interface/common.interface'
import { DEFAULT_API_PAGINATED_RESPONSE } from '@/constant/common'

export const getOrder = (params) => {
  const urlParams = getUrlParamsFromObject(params)
  if (isEmptyValue(params.searchTerm)) {
    return HttpRequest.get(`/orders?${urlParams}`)
  } else {
    return HttpRequest.get(`/orders/search-list?${urlParams}`)
  }
}

export const getOrdersAdvance = (params, dataPayload) => {
  const { page_number = 1, page_size = 10, sort = '', sortBy = '' } = params

  const {
    delivery_date,
    supplier,
    factories,
    style_ids,
    searchTerm = '',
    search,
    ...restParams // Capture any custom fields dynamically
  } = dataPayload

  const { yyyyMMDD } = DATE_FORMAT
  const getDeliveryDate = map(delivery_date, (dateJs) =>
    moment(dateJs).format(yyyyMMDD)
  )

  const queryParams = {
    page_number: page_number.toString(),
    page_size: page_size.toString(),
    sortBy,
    sort,
  }
  const toQueryParam = new URLSearchParams(
    omitBy(transformFieldEmptyToNull(queryParams), isEmpty)
  ).toString()

  // Build payload (filters only, remove empty values)
  const payload = omitBy(
    {
      supplier,
      factories,
      style_ids,
      delivery_date: join(getDeliveryDate, ','),
      search: search || searchTerm || null,
      ...restParams,
    },
    isEmpty
  )

  const url = `/orders/search-advance?${toQueryParam}`
  return HttpRequest.post(url, payload)
}

export const getGroupOrders = (params: {
  page_number: number
  page_size: number
  value: string
  data_type: string
  group: string
  status: string
}) => {
  const urlParams = getUrlParamsFromObject(params)
  return HttpRequest.get(`/orders/group?${urlParams}`)
}

export const getPropertyOrders = (params: {
  key: string
  value: string
  data_type: string
}) => HttpRequest.get(`/orders/property`, { params })

export async function deleteOrderRef(id: string) {
  return await HttpRequest.delete(`/orders/${id}`)
}

// TODO: REMOVE
export const getOrderCustomColumns = async (params: {
  page_number?: number
  page_size?: number
}) => {
  const queryParams = {
    page_number: params.page_number || 1,
    page_size: params.page_size || 10,
  }
  return await HttpRequest.get('/orders/advance_filter_custom_column', {
    params: queryParams,
  })
}

export const getOrderCustomFieldData = async (params: {
  page_number?: number
  page_size?: number
  field?: any
  search?: string
  name?: string
  mode?: 'myOrders' | 'myTasks' | 'myQuality' | ''
}) => {
  if (
    includes(['supplier', 'suppliers', 'factories', 'factory'], params.field)
  ) {
    let cvType: any = ''

    if (params.field === 'factories') {
      cvType = replace(params.field, 'factories', 'factory')
    }
    if (params.field === 'suppliers') {
      cvType = replace(params.field, 'suppliers', 'supplier')
    }

    const queryParams = {
      page_number: params.page_number || 1,
      page_size: params.page_size || 10,
      type: cvType,
      ...(params.search && { search: params.search }),
      ...(params.name && { name: params.name }),
    }
    const responseData = await getOrderSuppliers(queryParams)

    return responseData
  }

  if (includes(['style_ids'], params.field)) {
    const queryParams = {
      page_number: params.page_number || 1,
      page_size: params.page_size || 10,
      search: params.search,
    }
    const responseData = await getOrderStyleIds(queryParams)

    return responseData
  }

  const queryParams = {
    page_number: params.page_number || 1,
    page_size: params.page_size || 10,
    field: params.field,
    ...(params.search && { search: params.search }),
  }
  const responseData = await getOrderAdvanceCustom(queryParams)

  return responseData
}

// Order-specific API endpoints for advance filter
export const getOrderSuppliers = async (params: {
  page_number?: number
  page_size?: number
  type?: 'supplier' | 'factory'
  search?: string
  name?: string
}): Promise<ApiPaginatedResponse<any>> => {
  const queryParams = {
    page_number: params.page_number || 1,
    page_size: params.page_size || 10,
    type: params.type,
    ...(params.name && { name: params.name }),
    ...(params.search && { search: params.search }),
  }
  const response = await HttpRequest.get('/orders/suppliers', {
    params: queryParams,
  })

  if (!response) {
    return DEFAULT_API_PAGINATED_RESPONSE
  }

  const { data } = response

  return data
}

export const getOrderStyleIds = async (params: {
  page_number?: number
  page_size?: number
  search?: string
}): Promise<ApiPaginatedResponse<any>> => {
  const queryParams = {
    page_number: params.page_number || 1,
    page_size: params.page_size || 10,
    ...(params.search && { search: params.search }),
  }
  const response = await HttpRequest.get('/orders/styleIds', {
    params: queryParams,
  })
  if (!response) {
    return DEFAULT_API_PAGINATED_RESPONSE
  }

  const { data } = response

  return data
}

export const getOrderAdvanceCustom = async (
  params: any
): Promise<ApiPaginatedResponse<any>> => {
  const queryParams = {
    page_number: params.page_number || 1,
    page_size: params.page_size || 10,
    ...(params.search && { search: params.search }),
    ...(params.field && { field: params.field }),
  }
  const response = await HttpRequest.get(
    '/orders/advance_filter_custom_column',
    {
      params: queryParams,
    }
  )
  if (!response) {
    return DEFAULT_API_PAGINATED_RESPONSE
  }

  const { data } = response

  return data
}
